import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { ArrowLeft, Loader2, Pencil } from 'lucide-react';
import { MerchantFeeForm } from './MerchantFeeForm';

const TRANSACTION_TYPES = [
  { value: 'payment', label: 'Payment', methods: ['etransfer_request_money', 'etransfer_standard'] },
  { value: 'payout', label: 'Payout', methods: ['etransfer_send_money'] },
  { value: 'refund', label: 'Refund', methods: ['etransfer_send_money'] },
  { value: 'chargeback', label: 'Chargeback', methods: ['etransfer_request_money'] }
];

import { Merchant, MerchantFee } from '../../types';

const METHOD_LABELS = {
  etransfer_request_money: 'eTransfer Request Money',
  etransfer_standard: 'eTransfer Standard',
  etransfer_send_money: 'eTransfer Send Money',
};

const STATUS_LABELS = {
  approved: 'Approved',
  declined: 'Declined',
  returned: 'Returned',
};

export function MerchantDetails() {
  const { merchantId } = useParams();
  const [merchant, setMerchant] = useState<Merchant | null>(null);
  const [fees, setFees] = useState<MerchantFee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showFeeForm, setShowFeeForm] = useState(false);

  useEffect(() => {
    fetchMerchantAndFees();
  }, [merchantId]);

  // Skip fetching for new merchant form
  if (merchantId === 'new') {
    return null;
  }

  async function fetchMerchantAndFees() {
    try {
      const [merchantResult, feesResult] = await Promise.all([
        supabase.from('merchants').select('*').eq('id', merchantId).single(),
        supabase.from('merchant_fees').select('*').eq('merchant_id', merchantId),
      ]);

      if (merchantResult.error) throw merchantResult.error;
      if (feesResult.error) throw feesResult.error;

      setMerchant(merchantResult.data);
      setFees(feesResult.data || []);
    } catch (err) {
      console.error('Error fetching merchant details:', err);
      setError('Failed to load merchant details');
    } finally {
      setLoading(false);
    }
  }

  const getFeesByTypeAndMethod = (type: string, method: string) => {
    return fees.filter(fee => fee.type === type && fee.payment_method === method);
  };
  
  const getUniqueKey = (method: string, fee: MerchantFee) => {
    return `${method}-${fee.type}-${fee.status}`;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (error || !merchant) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">{error || 'Merchant not found'}</p>
        <Link to="/merchants" className="text-indigo-600 hover:text-indigo-500 mt-4 inline-flex items-center">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Merchants
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <Link to="/merchants" className="text-indigo-600 hover:text-indigo-500 inline-flex items-center mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Merchants
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">
            {merchant.name}
          </h1>
        </div>
        <button
          onClick={() => setShowFeeForm(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          <Pencil className="w-4 h-4 mr-2" />
          Edit Fees
        </button>
      </div>

      {TRANSACTION_TYPES.map((type) => (
        <div key={type.value} className="bg-white shadow rounded-lg mb-8">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
            <h2 className="text-lg font-medium text-gray-900">{type.label}</h2>
          </div>
          <div className="px-4 py-5 sm:p-6">
            <div className="space-y-8">
              {type.methods.map((method) => (
                <div key={method} className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-base font-medium text-gray-800 mb-4">
                    {METHOD_LABELS[method]}
                  </h3>
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
                    {Object.entries(STATUS_LABELS).map(([status, label]) => {
                      const fees = getFeesByTypeAndMethod(type.value, method);
                      const fee = fees.find(f => f.status === status);
                      return (
                        <div
                          key={`${type.value}-${method}-${status}`}
                          className="bg-white rounded-lg p-6 border border-gray-200"
                        >
                          <h4 className="text-sm font-medium text-gray-900 mb-4">
                            {label}
                          </h4>
                          <dl className="space-y-3">
                            <div>
                              <dt className="text-xs text-gray-500">Per Transaction</dt>
                              <dd className="text-sm font-medium text-gray-900">
                                ${fee?.per_transaction_fee.toFixed(2) || '0.00'}
                              </dd>
                            </div>
                            <div>
                              <dt className="text-xs text-gray-500">Rate Fee</dt>
                              <dd className="text-sm font-medium text-gray-900">
                                {fee?.rate_fee_percentage.toFixed(2) || '0.00'}%
                              </dd>
                            </div>
                            <div>
                              <dt className="text-xs text-gray-500">Max Fee Cap</dt>
                              <dd className="text-sm font-medium text-gray-900">
                                ${fee?.max_fee_cap.toFixed(2) || '0.00'}
                              </dd>
                            </div>
                          </dl>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}

      {showFeeForm && (
        <MerchantFeeForm
          merchant={merchant}
          onClose={() => {
            setShowFeeForm(false);
            fetchMerchantAndFees();
          }}
        />
      )}
    </div>
  );
}